﻿<template>
    <AuthPanel :title="title" :header="title" subheader="Something went wrong">
        <div class="flex flex-col items-center gap-6">
            <p class="m-0 text-center">{{ description }}</p>
            <UButton
                class="w-full justify-center mobile:w-auto"
                :variant="ButtonVariants.Dark"
                :label="buttonLabel"
                @click="handleError" />
        </div>
    </AuthPanel>
</template>

<script setup lang="ts">
    import type { NuxtError } from '#app';
    import { ButtonVariants } from '@constants/button-variants';
    import { SERVER_ERROR_TITLE } from '@constants/errors';
    import { HttpStatus } from '@constants/http-status';
    import { HOME } from '@constants/routes';

    const props = defineProps<{
        error: NuxtError;
    }>();

    const userSession = useUserSessionStore();

    const isSignedIn = computed(() => userSession.isAuthenticated && !userSession.hasAccessTokenExpired());

    const isPageUnavailable = computed(() =>
        [HttpStatus.Unauthorized, HttpStatus.Forbidden, HttpStatus.NotFound].some(
            (status) => status === props.error.status
        )
    );

    const buttonLabel = computed(() => {
        if (!isSignedIn.value) {
            return 'Go to sign in';
        }

        return isPageUnavailable.value ? 'Go to projects' : 'Retry page';
    });

    const title = computed(() => props.error.statusText || SERVER_ERROR_TITLE);

    const description = computed(() => {
        // data can be an array, a string or an JSON
        if (props.error.data) {
            if (isStringArray(props.error.data)) {
                return props.error.data[0];
            }

            if (typeof props.error.data === 'string') {
                try {
                    // If JSON, ignore it
                    JSON.parse(props.error.data);
                } catch {
                    return props.error.data;
                }
            }
        }

        if (props.error.status === HttpStatus.Unauthorized || props.error.status === HttpStatus.Forbidden) {
            return 'You are not authorized to access this page.';
        }

        if (props.error.status === HttpStatus.NotFound) {
            return 'This page is not found.';
        }

        return props.error.statusText || props.error.message;
    });

    function handleError() {
        if (!isSignedIn.value) {
            clearError({ redirect: useRuntimeConfig().public.auth.unauthorizedRedirectTo });
            return;
        }

        if (isPageUnavailable.value) {
            clearError({ redirect: HOME });
            return;
        }

        reloadNuxtApp({ force: true });
    }
</script>
